import { Subscribable } from '@volkswagen-onehub/feature-hub-utils';
// use aliases so we only have to apply changes here
import { BuildabilityStatusV2 as BuildabilityStatus } from './types';
import { BuildabilityCache } from './buildability-cache';
import { areConfigurationsCompatible, isCompleteConfiguration, isConfigurationValidForContext, isSameConfiguration } from './configuration-utils';
import { createBuildableConfiguration, isErrorResponse } from './create-buildable';
import { KEYS_IN_ORDER } from './keys';
import { normalizeConfiguration, normalizeModelId as normalizeModelIdFn } from './normalize';
import { validateConfiguration } from './validator';
export * from './types';
function getAutoBuildabilityStatus(configuration, buildabilityCache) {
    const cached = buildabilityCache.get(configuration);
    if (cached) {
        return cached.buildabilityStatus;
    }
    const hasConfiguration = isCompleteConfiguration(configuration);
    return hasConfiguration
        ? BuildabilityStatus.UNKNOWN
        : BuildabilityStatus.INCOMPLETE;
}
const NO_LOCKED_PARAMETERS = {
    category: false,
    carlineId: false,
    salesGroupId: false,
    trimName: false,
    modelId: false,
    modelYear: false,
    modelVersion: false,
    exteriorId: false,
    interiorId: false,
    options: false
};
export class CarConfigurationServiceV2Impl extends Subscribable {
    constructor(options, localeService, serviceConfigurationService) {
        super();
        this.options = options;
        this.localeService = localeService;
        this.serviceConfigurationService = serviceConfigurationService;
        this.buildabilityCache = new BuildabilityCache();
        this.configurationsPerContext = new Map();
        this.preliminaryConfigurations = [];
    }
    setDefaultContext(context, initialConfiguration, options = {}) {
        const preliminaries = [];
        const lastConfigurationForThisContext = context
            ? this.configurationsPerContext.get(context)
            : undefined;
        if (lastConfigurationForThisContext) {
            preliminaries.push(lastConfigurationForThisContext.configuration);
        }
        preliminaries.push(...this.preliminaryConfigurations);
        const configuration = preliminaries.reduce((currentConfiguration, preliminary) => {
            if (areConfigurationsCompatible(currentConfiguration, preliminary)) {
                return preliminary;
            }
            return currentConfiguration;
        }, (initialConfiguration || {}));
        this.preliminaryConfigurations = [];
        if (this.currentContext) {
            this.lastContext = this.currentContext;
        }
        else if (!this.lastContext && context) {
            this.lastContext = context;
        }
        // tslint:disable-next-line:triple-equals
        const hasDefaultContextChanged = context != this.currentContext;
        this.currentContext = context || undefined;
        let lockedParameters = NO_LOCKED_PARAMETERS;
        if (options && options.lockedParameter) {
            const lockedIndex = KEYS_IN_ORDER.indexOf(options.lockedParameter);
            lockedParameters = KEYS_IN_ORDER.reduce((buffer, key, index) => (Object.assign(Object.assign({}, buffer), { [key]: index <= lockedIndex })), lockedParameters);
        }
        this.setInternal('setDefaultContext', context, 
        // tslint:disable-next-line:no-any
        configuration ? configuration : {}, lockedParameters, Object.assign(Object.assign({}, options), { hasDefaultContextChanged }));
    }
    getContext(context = this.currentContext) {
        if (context === undefined) {
            return null;
        }
        const contextParameters = this.configurationsPerContext.get(context);
        if (!contextParameters) {
            return null;
        }
        return {
            context,
            lockedParameters: contextParameters.lockedParameters
        };
    }
    getLastContext() {
        return this.getContext(this.lastContext);
    }
    set(configuration, context = this.currentContext) {
        if (!context) {
            // Default context should be used and no default context was set yet
            // add to buildability cache anyways
            if (configuration.buildabilityStatus) {
                this.cacheConfigurationWithDefiniteBuildState(configuration);
            }
            this.preliminaryConfigurations.push(configuration);
            return;
        }
        const currentContextData = this.configurationsPerContext.get(context);
        const lockedParameters = currentContextData
            ? currentContextData.lockedParameters
            : NO_LOCKED_PARAMETERS;
        this.setInternal('set', context, configuration, lockedParameters);
    }
    get(context = this.currentContext) {
        if (!context) {
            return null;
        }
        const contextParameters = this.configurationsPerContext.get(context);
        if (!contextParameters) {
            return null;
        }
        // return a copy so config can not be changed
        return Object.assign({}, contextParameters.configuration);
    }
    async createBuildable(context) {
        const useDefaultContext = !context;
        const defaultContext = this.currentContext;
        if (useDefaultContext && defaultContext === undefined) {
            return {
                error: 'NO_CONTEXT'
            };
        }
        // If context is undefined, we use the default context. Both contexts were already checked above
        // tslint:disable-next-line:no-non-null-assertion
        const safeContext = useDefaultContext ? defaultContext : context;
        const contextParameters = this.configurationsPerContext.get(safeContext);
        if (!contextParameters) {
            return {
                error: 'NO_CONTEXT'
            };
        }
        const { configuration } = contextParameters;
        if (configuration.buildabilityStatus === BuildabilityStatus.BUILDABLE) {
            return { configuration };
        }
        const previousResponse = this.getDefiniteBuildabilityResponseFromCache(configuration);
        if (previousResponse) {
            // Make sure that there is asynchronous action in this branch of execution to avoid problems with timing
            await Promise.resolve();
            if (!isErrorResponse(previousResponse)) {
                this.setInternal('createBuildable', safeContext, previousResponse.configuration);
            }
            return previousResponse;
        }
        // TODO NGWD6-335: check if requests are already running and don't start new ones
        let lastWrite;
        let setWatcherSubscription;
        const setWatcher = new Promise(resolve => {
            setWatcherSubscription = this.subscribe(() => {
                const newConfiguration = this.get(safeContext);
                if (newConfiguration && newConfiguration !== configuration) {
                    lastWrite = newConfiguration;
                    if (newConfiguration.buildabilityStatus === BuildabilityStatus.BUILDABLE) {
                        resolve({ configuration: newConfiguration });
                    }
                }
            });
        });
        const response = await Promise.race([
            createBuildableConfiguration(this.serviceConfigurationService, this.localeService.language, this.options.serviceTimeout, configuration),
            setWatcher
        ]);
        if (setWatcherSubscription) {
            setWatcherSubscription.unsubscribe();
        }
        if (isErrorResponse(response)) {
            if (response.error === 'IMPOSSIBLE') {
                this.buildabilityCache.putResult(configuration, response);
            }
            return response;
        }
        this.buildabilityCache.putResult(configuration, response);
        if (useDefaultContext) {
            // If there was a default context before, it cannot be unset
            // If we reach this point there was a default context, otherwise a NO_CONTEXT error would have been returned already
            // tslint:disable-next-line:no-non-null-assertion
            const newDefaultContext = this.currentContext;
            if (newDefaultContext !== defaultContext) {
                // make sure that the configuration is set into the old context anyways
                this.setInternal('createBuildable', safeContext, response.configuration);
                return {
                    error: 'CONTEXT_CHANGE',
                    previousContext: safeContext,
                    newContext: newDefaultContext
                };
            }
        }
        if (lastWrite &&
            !areConfigurationsCompatible(lastWrite, response.configuration)) {
            return {
                error: 'INCOMPATIBLE_WRITE',
                configuration: lastWrite
            };
        }
        this.setInternal('createBuildable', safeContext, response.configuration);
        return response;
    }
    normalizeModelId(modelId) {
        return normalizeModelIdFn(modelId);
    }
    getDefiniteBuildabilityResponseFromCache(configuration) {
        const cached = this.buildabilityCache.get(configuration);
        return cached && cached.result;
    }
    normalizeConfigurationWithBuildability(configuration) {
        const normalized = normalizeConfiguration(configuration);
        const buildabilityStatus = configuration.buildabilityStatus ||
            getAutoBuildabilityStatus(normalized, this.buildabilityCache);
        return Object.assign(Object.assign({}, normalized), { buildabilityStatus });
    }
    setInternal(eventPayload, context, newConfiguration, lockedParameterDefault, options) {
        const missingFields = validateConfiguration(newConfiguration);
        if (missingFields === undefined) {
            throw new Error('configuration is missing');
        }
        if (missingFields.length) {
            throw new Error(`configuration is missing these fields to be valid: ${missingFields.join(',')}`);
        }
        const previousContextParameters = context
            ? this.configurationsPerContext.get(context)
            : undefined;
        const normalizedConfiguration = this.normalizeConfigurationWithBuildability(newConfiguration);
        this.cacheConfigurationWithDefiniteBuildState(normalizedConfiguration);
        if (!(options === null || options === void 0 ? void 0 : options.hasDefaultContextChanged) &&
            previousContextParameters &&
            isSameConfiguration(previousContextParameters.configuration, normalizedConfiguration)) {
            // nothing changed, so don't change anything
            return;
        }
        // if options are passed we use them. If there are none we get them from the
        // previous context. If we are missing even that we set a default
        const defaultOptions = options ||
            (previousContextParameters && previousContextParameters.options) ||
            {};
        const lockedParameters = previousContextParameters
            ? previousContextParameters.lockedParameters
            : lockedParameterDefault || NO_LOCKED_PARAMETERS;
        if (previousContextParameters &&
            !isConfigurationValidForContext(previousContextParameters.configuration, normalizedConfiguration, previousContextParameters.lockedParameters)) {
            throw new Error('New configuration cannot override locked parameters');
        }
        if (context) {
            this.configurationsPerContext.set(context, {
                configuration: normalizedConfiguration,
                lockedParameters,
                options: defaultOptions
            });
        }
        this.updateInternal(eventPayload);
    }
    cacheConfigurationWithDefiniteBuildState(configuration) {
        const { buildabilityStatus } = configuration;
        if (buildabilityStatus === BuildabilityStatus.BUILDABLE ||
            buildabilityStatus === BuildabilityStatus.NOT_BUILDABLE) {
            this.buildabilityCache.putStatus(configuration, buildabilityStatus);
        }
    }
}
